<script setup lang="ts">
    const FOCUSABLE_ELEMENT_SELECTOR = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    const { t, te } = useI18n();
    const { visibleBand, close, returnToPreviousArea } = useAreaSwitchBand();

    const bandElement = useTemplateRef<HTMLElement>('bandElement');

    // Hide the band when the translation is missing. Otherwise the visitor sees the raw key.
    const label = computed(() => {
        const labelKey = visibleBand.value?.labelKey;

        return labelKey && te(labelKey) ? t(labelKey) : null;
    });

    /** A modified click (for example Ctrl or Cmd) opens the link in a new tab. The current tab stays in its area. */
    const handleBackLinkClick = (event: MouseEvent) => {
        const isPlainPrimaryClick =
            event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

        if (isPlainPrimaryClick) returnToPreviousArea();
    };

    /**
     * The close button is removed from the page. Without this step, the focus goes to the page body.
     * The focus moves to the next element in the tab order of the header, but only when the close button had the focus.
     */
    const closeBand = async () => {
        const header = bandElement.value?.closest('header');
        const hadFocus = bandElement.value?.contains(document.activeElement) ?? false;

        close();

        if (!hadFocus) return;

        await nextTick();

        // The browser ignores `focus()` on a hidden element, so the first element that gets the focus is visible.
        for (const element of header?.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR) ?? []) {
            element.focus();
            if (document.activeElement === element) return;
        }
    };
</script>

<template>
    <div
        v-if="visibleBand && label"
        ref="bandElement"
        class="area-switch-band"
        data-testid="area-switch-band"
    >
        <div class="content">
            <NuxtLink
                :to="visibleBand.backUrl"
                class="back-link"
                data-testid="area-switch-band-back-link"
                @click="handleBackLinkClick"
            >
                <Icon
                    name="lucide:circle-arrow-left"
                    size="24px"
                    class="back-link-icon"
                />
                <span
                    class="back-link-label"
                    :title="label"
                >
                    {{ label }}
                </span>
            </NuxtLink>

            <SharedCloseButton
                class="band-close-button"
                :label="$t('general.close')"
                @click="closeBand"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    // The sizes in px are from the Figma wireframe "Variante 1: Headerband" (node 3227-12825).
    .area-switch-band {
        width: 100%;
        height: 47px;
        background-color: var(--color-petrol-20);
    }

    .content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        height: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
        padding: 0 var(--spacing);

        // Same side padding as the header content container.
        @include header-wide {
            padding: 0;
        }

        @include header-desktop {
            justify-content: flex-end;
        }
    }

    .back-link {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        height: 32px;
        padding: 0 var(--spacing) 0 6px;
        border: 1px solid var(--black);
        border-radius: 10px;
        color: var(--black);
        font-size: var(--font-s);
        font-weight: 500;
        line-height: 1.25;
        text-decoration: none;

        @media (hover: hover) {
            &:hover {
                background-color: var(--white);
            }
        }

        &:active {
            background-color: color-mix(in srgb, var(--color-petrol) 35%, white 65%);
        }
    }

    // The global focus ring (--color-focus) has too little contrast on the petrol background.
    .back-link:focus-visible,
    .band-close-button:focus-visible {
        outline: 2px solid var(--black);
        outline-offset: 2px;
        box-shadow: none;
    }

    .back-link-icon {
        flex-shrink: 0;
        color: inherit;
    }

    .back-link-label {
        overflow: hidden;
        font-size: inherit;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .band-close-button {
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: var(--border-radius-circle);
        background-color: var(--black);
        color: var(--white);

        // The X icon is 21.6px in the wireframe. This is the rounded value.
        :deep(svg) {
            width: 22px;
            height: 22px;
        }

        @media (hover: hover) {
            &:hover {
                background-color: var(--color-petrol);
            }
        }

        &:active {
            background-color: color-mix(in srgb, var(--color-petrol) 70%, black 30%);
        }
    }
</style>
